import { useEffect } from "react";
import axios from "axios";
import { Capacitor } from "@capacitor/core";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * usePushNotifications — registers the device with FCM and posts the token to backend
 * so the astrologer can push a notification when they reply.
 *
 * Only runs on a native platform (Android / iOS via Capacitor). No-op on web.
 */
export default function usePushNotifications(user) {
  useEffect(() => {
    let mounted = true;
    if (!user || !Capacitor.isNativePlatform()) return;

    (async () => {
      try {
        // Dynamic import so the web bundle doesn't fail when @capacitor/push-notifications
        // is not available (web preview).
        const { PushNotifications } = await import("@capacitor/push-notifications");

        // Request permission
        const perm = await PushNotifications.requestPermissions();
        if (perm.receive !== "granted") return;

        // Register with FCM / APNS
        await PushNotifications.register();

        // Listener: registration succeeded -> send token to backend
        PushNotifications.addListener("registration", async (token) => {
          if (!mounted) return;
          try {
            await axios.post(
              `${API}/push/register-token`,
              { token: token.value, platform: Capacitor.getPlatform() },
              { withCredentials: true }
            );
          } catch (e) {
            console.warn("Push token register failed", e);
          }
        });

        // Optional: log errors
        PushNotifications.addListener("registrationError", (err) => {
          console.warn("Push registrationError", err);
        });

        // Foreground message arrives -> we just log; the OS handles the notification banner.
        PushNotifications.addListener("pushNotificationReceived", (notif) => {
          console.log("push received", notif);
        });

        // User tapped the notification -> route into the relevant page if needed
        PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
          const data = action?.notification?.data || {};
          if (data?.route && typeof window !== "undefined") {
            window.location.href = data.route;
          }
        });
      } catch (e) {
        // Plugin not installed (web) — silently ignore
      }
    })();

    return () => { mounted = false; };
  }, [user]);
}
